import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Col, Container, Dropdown, Form, Pagination, Row, Table } from "react-bootstrap";
import { fetchCompanyAudit, fetchCompanyAuditEntry } from "../../utils/http/companies";
import { HamburgerToggle, JsonModal, normaliseResponse, formatDate, unwrapId } from "./helpers";

// Entity types available in the audit log. Kept as a static list so the filter
// dropdown works even before any rows have loaded.
const entityTypes = [
  "listing",
  "contact",
  "search_criteria",
  "task",
  "company",
  "mapping",
  "user",
  "office",
  "representative",
];

function AuditLogs({
  companyNameOrId,
  initialEntityType = "",
  initialEntityId = "",
}: {
  companyNameOrId: string;
  initialEntityType?: string;
  initialEntityId?: string;
}) {
  const [entityTypeFilter, setEntityTypeFilter] = useState<string>(initialEntityType);
  const [entityIdFilter, setEntityIdFilter] = useState<string>(initialEntityId);
  const [debouncedEntityIdFilter, setDebouncedEntityIdFilter] = useState<string>(initialEntityId);
  const [actionFilter, setActionFilter] = useState<string>("");
  const [sourceSystemIdFilter, setSourceSystemIdFilter] = useState<string>("");
  const [debouncedSourceSystemIdFilter, setDebouncedSourceSystemIdFilter] = useState<string>("");
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize] = useState<number>(50);
  const [selectedAuditId, setSelectedAuditId] = useState<string | null>(null);

  // Debounce the free-text filters so we don't fire a request per keystroke.
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSourceSystemIdFilter(sourceSystemIdFilter);
      setCurrentPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [sourceSystemIdFilter]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedEntityIdFilter(entityIdFilter);
      setCurrentPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [entityIdFilter]);

  const { data, isLoading, error } = useQuery({
    queryKey: [
      "company-audit",
      companyNameOrId,
      entityTypeFilter,
      debouncedEntityIdFilter,
      actionFilter,
      debouncedSourceSystemIdFilter,
      currentPage,
      pageSize,
    ],
    queryFn: ({ signal }) =>
      fetchCompanyAudit({
        companyNameOrId,
        entityType: entityTypeFilter || undefined,
        entityId: debouncedEntityIdFilter || undefined,
        action: actionFilter || undefined,
        sourceSystemId: debouncedSourceSystemIdFilter || undefined,
        page: currentPage,
        size: pageSize,
        signal,
      }),
    staleTime: 2000,
  });

  const { items, total } = normaliseResponse(data);
  const totalPages = Math.ceil(total / pageSize);

  const {
    data: selectedEntry,
    isLoading: isLoadingEntry,
    error: entryError,
  } = useQuery({
    queryKey: ["company-audit-entry", companyNameOrId, selectedAuditId],
    queryFn: ({ signal }) =>
      fetchCompanyAuditEntry({
        companyNameOrId,
        auditId: selectedAuditId!,
        signal,
      }),
    enabled: !!selectedAuditId,
  });

  const handleEntityTypeChange = (value: string) => {
    setEntityTypeFilter(value);
    setCurrentPage(1);
  };

  const handleActionChange = (value: string) => {
    setActionFilter(value);
    setCurrentPage(1);
  };

  return (
    <div>
      <Container className="pb-3 px-0">
        <Row className="mb-2">
          <Col md={3}>
            <Form.Group>
              <Form.Label style={{ fontSize: "0.9em", marginBottom: "0.25rem" }}>Entity Type</Form.Label>
              <Form.Select
                size="sm"
                value={entityTypeFilter}
                onChange={(e) => handleEntityTypeChange(e.target.value)}
                style={{ border: "1px solid #999999" }}
              >
                <option value="">- All entity types -</option>
                {entityTypes
                  .slice()
                  .sort((a, b) => a.localeCompare(b))
                  .map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={2}>
            <Form.Group>
              <Form.Label style={{ fontSize: "0.9em", marginBottom: "0.25rem" }}>Action</Form.Label>
              <Form.Select
                size="sm"
                value={actionFilter}
                onChange={(e) => handleActionChange(e.target.value)}
                style={{ border: "1px solid #999999" }}
              >
                <option value="">- All actions -</option>
                <option value="create">create</option>
                <option value="update">update</option>
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group>
              <Form.Label style={{ fontSize: "0.9em", marginBottom: "0.25rem" }}>Source System ID</Form.Label>
              <Form.Control
                size="sm"
                type="text"
                placeholder="Search by source system id..."
                value={sourceSystemIdFilter}
                onChange={(e) => setSourceSystemIdFilter(e.target.value)}
                style={{ border: "1px solid #999999" }}
              />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group>
              <Form.Label style={{ fontSize: "0.9em", marginBottom: "0.25rem" }}>Entity ID</Form.Label>
              <Form.Control
                size="sm"
                type="text"
                placeholder="Search by entity id..."
                value={entityIdFilter}
                onChange={(e) => setEntityIdFilter(e.target.value)}
                style={{ border: "1px solid #999999" }}
              />
            </Form.Group>
          </Col>
        </Row>
        <Row>
          <Col>
            <span className="text-muted">
              Showing {items.length > 0 ? (currentPage - 1) * pageSize + 1 : 0} to{" "}
              {Math.min((currentPage - 1) * pageSize + items.length, total)} of {total} entries
            </span>
          </Col>
        </Row>
      </Container>
      {error ? (
        <div className="text-danger mb-3">
          {(error as any)?.message || "An error occurred while fetching the audit log."}
        </div>
      ) : null}
      <Table striped bordered hover>
        <thead>
          <tr>
            <th style={{ width: "1%", whiteSpace: "nowrap" }}></th>
            <th style={{ width: "1%", whiteSpace: "nowrap" }}>Action</th>
            <th style={{ width: "1%", whiteSpace: "nowrap" }}>Created At</th>
            <th style={{ width: "1%", whiteSpace: "nowrap" }}>Entity Type</th>
            <th style={{ width: "1%", whiteSpace: "nowrap" }}>Source System ID</th>
            <th>Fields</th>
          </tr>
        </thead>
        <tbody>
          {isLoading ? (
            <tr>
              <td colSpan={6} className="text-center text-muted">
                Loading...
              </td>
            </tr>
          ) : items.length === 0 ? (
            <tr>
              <td colSpan={6} className="text-center text-muted">
                No audit entries found.
              </td>
            </tr>
          ) : (
            items.map((entry: any, index: number) => {
              const action = entry?.action;
              // Only updates have a meaningful changed-fields list; creates are hidden.
              const changedFields: string[] =
                action === "update" && Array.isArray(entry?.changed_fields) ? entry.changed_fields : [];
              const auditId = unwrapId(entry?._id);
              return (
                <tr key={auditId !== "-" ? auditId : `${entry?.source_system_id}-${index}`}>
                  <td style={{ width: "1%", whiteSpace: "nowrap", textAlign: "center" }}>
                    {auditId !== "-" && (
                      <Dropdown>
                        <Dropdown.Toggle as={HamburgerToggle} id={`audit-actions-${auditId}`} />
                        <Dropdown.Menu>
                          <Dropdown.Item onClick={() => setSelectedAuditId(auditId)}>
                            <i className="bi bi-braces me-2"></i>
                            View JSON
                          </Dropdown.Item>
                        </Dropdown.Menu>
                      </Dropdown>
                    )}
                  </td>
                  <td style={{ width: "1%", whiteSpace: "nowrap" }}>
                    <span className={`badge bg-${action === "create" ? "success" : "info"}`}>{action ?? "-"}</span>
                  </td>
                  <td style={{ width: "1%", whiteSpace: "nowrap" }}>{formatDate(entry?.created_at)}</td>
                  <td style={{ width: "1%", whiteSpace: "nowrap" }}>{entry?.entity_type ?? "-"}</td>
                  <td style={{ width: "1%", whiteSpace: "nowrap" }}>{entry?.source_system_id ?? "-"}</td>
                  <td>{changedFields.length > 0 ? changedFields.join(", ") : "-"}</td>
                </tr>
              );
            })
          )}
        </tbody>
      </Table>
      <Container className="pb-3">
        <Row>
          <Col className="d-flex justify-content-center">
            {totalPages > 1 && (
              <Pagination>
                <Pagination.First onClick={() => setCurrentPage(1)} disabled={currentPage === 1} />
                <Pagination.Prev onClick={() => setCurrentPage(currentPage - 1)} disabled={currentPage === 1} />

                {Array.from({ length: totalPages }, (_, index) => {
                  const page = index + 1;
                  if (page === 1 || page === totalPages || (page >= currentPage - 2 && page <= currentPage + 2)) {
                    return (
                      <Pagination.Item key={page} active={page === currentPage} onClick={() => setCurrentPage(page)}>
                        {page}
                      </Pagination.Item>
                    );
                  } else if (page === currentPage - 3 || page === currentPage + 3) {
                    return <Pagination.Ellipsis key={`ellipsis-${page}`} disabled />;
                  }
                  return null;
                }).filter(Boolean)}

                <Pagination.Next
                  onClick={() => setCurrentPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                />
                <Pagination.Last onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} />
              </Pagination>
            )}
          </Col>
        </Row>
      </Container>

      <JsonModal
        show={!!selectedAuditId}
        onHide={() => setSelectedAuditId(null)}
        title="Audit Entry"
        isLoading={isLoadingEntry}
        error={entryError}
        value={selectedEntry}
      />
    </div>
  );
}

export default AuditLogs;
